// @ts-nocheck
import { useMsal } from '@azure/msal-react'
import { NEW_LANDINGI_URL } from '@config/env'
import { BEGINNING, COMPLETED } from '@constants/registrationSteps'
import { getCanonicalTimeZone } from '@helpers/date'
import { getErrorMessage } from '@helpers/errorMessages'
import useQuery from '@hooks/useQuery'
import MicrosoftIcon from '@images/registration/microsoft_icon.svg'
import { useRegisterContext } from '@pages/Authentication/contexts/register'
import { useStepsContext } from '@pages/Authentication/contexts/steps'
import { isLastStep } from '@pages/Authentication/helpers/steps'
import { REGISTRATION } from '@routes/path'
import {
  changeRegistrationStep,
  getAuthInfo,
  loginWithOAuth,
  registerWithProvider
} from '@services/authentication'
import { Button, emitTimingToast, Image, Spreader, Text } from '@ui-kit'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Row } from 'simple-flexbox'
import { useSWRConfig } from 'swr'

export const MicrosoftSignUpButton = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const { startFromFirstStep } = useStepsContext()

  const {
    chosenPackage,
    source,
    pricing,
    newSignup,
    isCustomOffer,
    lang,
    period,
    isDefaultRegistration
  } = useRegisterContext()

  const queryParams = useQuery()
  const navigate = useNavigate()
  const { mutate } = useSWRConfig()

  const { instance } = useMsal()

  const handleSignUp = useCallback(async () => {
    try {
      const response = await instance.loginPopup({
        scopes: ['user.read', 'email', 'profile', 'openid'],
        prompt: 'select_account'
      })

      const { accessToken, idToken } = response
      const timeZone = getCanonicalTimeZone()
      const uri = window.location.href

      await registerWithProvider({
        token: accessToken,
        idToken,
        timeZone,
        packageRecordKey: chosenPackage,
        uri,
        source,
        pricing,
        newSignup,
        isCustomOffer,
        promotion: queryParams.get('promotion') || undefined,
        defaultRegistration: isDefaultRegistration ? 'yes' : 'no',
        language: lang,
        provider: 'microsoft'
      })

      await loginWithOAuth(accessToken, idToken, 'microsoft', NEW_LANDINGI_URL)

      const { data: authInfo } = await getAuthInfo()

      startFromFirstStep()

      const { flow } = authInfo
      const { steps } = flow

      await changeRegistrationStep(BEGINNING)

      if (isLastStep(steps, BEGINNING)) {
        await changeRegistrationStep(COMPLETED)

        mutate('payments/discount', undefined, { revalidate: true })
        mutate('auth')

        return
      }

      mutate('auth')
      navigate(`${REGISTRATION.SURVEY.DEFAULT}?lang=${lang}&period=${period}`)
    } catch (error) {
      const { response } = error

      const code = response?.data.error.code

      emitTimingToast({
        message: t(getErrorMessage(code)),
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    instance,
    chosenPackage,
    isCustomOffer,
    isDefaultRegistration,
    lang,
    mutate,
    navigate,
    newSignup,
    period,
    pricing,
    queryParams,
    source,
    startFromFirstStep,
    t
  ])

  return (
    <Button
      variant='outlined'
      onClick={handleSignUp}
      size={16}
      disabled={isLoading}
      isLoading={isLoading}
      data-testid='microsoft-signup-button'
    >
      <Row alignItems='center' justifyContent='center'>
        <Image src={MicrosoftIcon} width='16px' height='16px' />

        <Spreader spread={10} />

        <Text size={16} weight={600} color='primary' lineHeight='normal'>
          {t('registration.flow.sign.up.microsoft')}
        </Text>
      </Row>
    </Button>
  )
}

MicrosoftSignUpButton.displayName = 'MicrosoftSignUpButton'
